import ReactEcharts from 'echarts-for-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { BasicTable, ContentCard } from '@/components/customComponents'

import {
  pieChartOptions,
  stackedBarChartOptions,
} from '@/constants/CHART_CONSTANTS'
import { useDiscountsItems } from '@/hooks'
import useDiscountPersona from '@/hooks/clarityLens/salesLens/discounts/useDiscountPersona'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxSalesLensFilter,
} from '@/store'
import { dateToString } from '@/utils'

interface DiscountTabProps {
  itemName: string
}

export const DiscountTab = ({ itemName }: DiscountTabProps) => {
  const { t } = useTranslation()
  const { selectedBranch, date, sizeFilter, temperatureFilter } =
    useReduxSalesLensFilter()
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('sales-lens-item')
  )

  const {
    discountsItemsData,
    BASE_DISCOUNTS_ITEMS_COLUMNS,
    categoryByCountChartData,
    categoryByAmountChartData,
    loading: discountsItemsLoading,
    refetch: discountsItemsRefetch,
  } = useDiscountsItems(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName,
    sizeFilter,
    temperatureFilter
  )

  const {
    discountPersonaChartData,
    loading: discountPersonaLoading,
    refetch: discountPersonaRefetch,
  } = useDiscountPersona(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing discounts items...')
      discountsItemsRefetch()
      discountPersonaRefetch()
    }
  }, [refreshTrigger, discountsItemsRefetch, discountPersonaRefetch])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <ContentCard
          title={t('clarityLens.salesLens.charts.discountUsageByPersona')}
          loading={discountPersonaLoading}
          className="col-span-6"
        >
          {discountPersonaChartData && (
            <ReactEcharts
              option={getStackedBarChartData(discountPersonaChartData)}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.salesLens.charts.discountShareInRevenue')}
          loading={discountsItemsLoading}
          className="col-span-3"
          isEmpty={
            !categoryByAmountChartData ||
            Object.keys(categoryByAmountChartData.data || {}).length === 0
          }
        >
          {categoryByAmountChartData && (
            <ReactEcharts
              option={getPieChartData(
                categoryByAmountChartData.data,
                categoryByAmountChartData.labels
              )}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.salesLens.charts.discountShareInTransactions')}
          loading={discountsItemsLoading}
          className="col-span-3"
          isEmpty={
            !categoryByCountChartData ||
            Object.keys(categoryByCountChartData.data || {}).length === 0
          }
        >
          {categoryByCountChartData && (
            <ReactEcharts
              option={getPieChartData(
                categoryByCountChartData.data,
                categoryByCountChartData.labels
              )}
            />
          )}
        </ContentCard>
      </div>

      <div>
        <BasicTable
          title={t('clarityLens.salesLens.charts.discountTypes')}
          data={discountsItemsData}
          columns={BASE_DISCOUNTS_ITEMS_COLUMNS}
          loading={discountsItemsLoading}
          enablePagination={true}
          itemsPerPage={5}
        />
      </div>
    </div>
  )
}

const getStackedBarChartData = (primaryData: {
  rawData: number[][]
  seriesNames: string[]
  xAxisNames: string[]
}) => {
  return stackedBarChartOptions(
    primaryData.rawData,
    primaryData.seriesNames,
    primaryData.xAxisNames,
    '40%',
    'money'
  )
}

const getPieChartData = (dataObj: Record<string, number>, labels: string[]) => {
  const data = labels.map((label) => dataObj[label.toLowerCase()] ?? 0)
  return pieChartOptions(data, labels)
}
